/** @jsx jsx */
import React, { useCallback, useMemo } from 'react';
import { jsx } from '@compiled/react';
import { useIntl } from '@atlassian/jira-intl';
import { updateFilterDialogEntryPoint } from '@atlassian/jira-save-filter-dialog/src/ui/update-filter-dialog/entrypoint.tsx';
import { JiraFilterAri } from '@atlassian/ari/jira/filter';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useActivationId } from '@atlassian/jira-tenant-context-controller/src/components/activation-id/index.tsx';
import {
	initiate,
	cancelled,
	dialogShown,
	entityUpdated,
} from '@atlassian/jira-directory-base/src/state/actions/operation/index.tsx';
import { EDIT } from '@atlassian/jira-directory-base/src/model/operation-types.tsx';
import { useDirectoryDispatch } from '@atlassian/jira-directory-base/src/common/remapped-redux/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { ModalEntryPointDropdownItemTrigger } from '@atlassian/jira-modal-entry-point-dropdown-item-trigger/src/ModalEntryPointDropdownItemTrigger.tsx';
import messages from '../messages.tsx';

export const UpdateFilterModal = ({
	filterId,
	returnFocusRef,
}: {
	filterId: string;
	returnFocusRef: React.RefObject<HTMLButtonElement>;
}): JSX.Element => {
	const cloudId = useCloudId();
	const activationId = useActivationId();
	const dispatch = useDirectoryDispatch();
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const entryPointProps = useMemo(
		() => ({
			filterId,
			onDialogShown: () => {
				dispatch(initiate({ id: filterId, operation: EDIT, childEntityId: undefined }));
				dispatch(dialogShown({ id: filterId, operation: EDIT, childEntityId: undefined }));
			},
			onSuccess: () => dispatch(entityUpdated({ id: filterId, operation: EDIT })),
			onClose: () => {
				dispatch(cancelled({ id: filterId, operation: EDIT }));
				requestAnimationFrame(() => {
					returnFocusRef.current?.focus();
				});
			},
		}),
		[filterId, dispatch, returnFocusRef],
	);

	const entryPointParams = useMemo(
		() => ({
			filterAri: JiraFilterAri.create({
				activationId,
				siteId: cloudId,
				filterId,
			}).toString(),
		}),
		[filterId, cloudId, activationId],
	);

	const onClick = useCallback(
		() =>
			fireUIAnalytics(
				createAnalyticsEvent({
					action: 'clicked',
					actionSubject: 'dropdownItem',
				}),
				'editDropdownItem',
			),
		[createAnalyticsEvent],
	);

	return (
		<ModalEntryPointDropdownItemTrigger
			modalProps={{
				width: 'medium',
				shouldScrollInViewport: true,
				shouldReturnFocus: false,
			}}
			entryPoint={updateFilterDialogEntryPoint}
			entryPointProps={entryPointProps}
			entryPointParams={entryPointParams}
			interactionName="jiraSaveFilterDialog.UpdateFilterDetails"
			onClick={onClick}
			useInternalModal
		>
			{formatMessage(messages.editFilter)}
		</ModalEntryPointDropdownItemTrigger>
	);
};
